import Image from 'next/image';
import React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { FontStyle } from '@/utils/StyleUtil';
import LabelUsingAffixIcon from '../ContextPrinter/LabelUsingAffixIcon';
import SvgSelector from '../Svg/SvgSelector';
import useFileAttachment from '@/hooks/common/useFileAttachment';
import useCheckCameraPermission from '@/hooks/common/useCheckCameraPermission';
import { isAppState } from '@/recoil';
import Seperator from '../Spacing/Seperator';

interface Props {
    uploadImages: { file: File; uploadSuccess: boolean }[];
    fileLength: number;
    onAddFiles: (_file: File) => void;
    onDeleteFiles: (_idx: number) => void;
}

const FileAttachment = ({ uploadImages, fileLength, onAddFiles, onDeleteFiles }: Props) => {
    const { handleChangeFileList } = useFileAttachment({
        uploadImages,
        onAddFiles,
        fileLength,
    });
    const { checkCameraPermission } = useCheckCameraPermission();

    const isApp = useRecoilValue(isAppState);

    const handleClickFileAttachment = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        if (isApp) checkCameraPermission(e);
    };

    return (
        <>
            <FileAttachmentText>{`파일 첨부 (최대 ${fileLength}개 첨부 가능)`}</FileAttachmentText>
            <FileContainer>
                <label htmlFor="file">
                    <SvgSelector
                        svg="plus"
                        width={12}
                        height={12}
                        stroke="#0EC4CF"
                        strokeWidth={2}
                        style={{ marginRight: '4px' }}
                    />
                    파일 첨부
                </label>
                <input type="file" id="file" onChange={handleChangeFileList} onClick={handleClickFileAttachment} />
            </FileContainer>
            {uploadImages.length > 0 && (
                <FileListContainer>
                    {uploadImages.map((file: { file: File; uploadSuccess: boolean }, index: number) => (
                        <React.Fragment key={file.file.name}>
                            <SpecificFileContainer uploadSuccess={file.uploadSuccess}>
                                <LabelUsingAffixIcon
                                    prefixUri={
                                        <Image src="/img/myPage/image@3x.png" alt="file" width={18} height={18} />
                                    }
                                    label={file.file.name}
                                    labelStyle={{
                                        width: '180px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                    }}
                                    suffixUri={
                                        file.uploadSuccess ? (
                                            <SuffixIcon onClick={() => onDeleteFiles(index)}>
                                                <SvgSelector
                                                    svg="close"
                                                    width={12}
                                                    height={12}
                                                    stroke="#888888"
                                                    strokeWidth={1.4}
                                                />
                                            </SuffixIcon>
                                        ) : undefined
                                    }
                                />
                            </SpecificFileContainer>
                            {uploadImages.length - 1 !== index && <Seperator height={12} />}
                        </React.Fragment>
                    ))}
                </FileListContainer>
            )}
            <Seperator height={8} />
            <FileLimitText>{'파일은 최대 용량 25MB까지 이미지 파일만 첨부 가능 합니다.'}</FileLimitText>
        </>
    );
};

const FileAttachmentText = styled.div`
    ${FontStyle(16, 24, '500', '#333333', -1)};
`;

const FileContainer = styled.div`
    margin: 14px 0 0 0;
    ${FontStyle(14, 22, '500', '#0ec4cf', -1)};

    label {
        ${({ theme }) => theme.flex.flexOnly};
        ${({ theme }) => theme.flex.justifyCenter};
        width: 100%;
        height: 46px;
        border: 1px solid #f2fafb;
        border-radius: 6px;
        background-color: #f0fbfc;
        color: ${({ theme }) => theme.colors.primary};
    }

    input {
        position: absolute;
        width: 0;
        height: 0;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
    }
`;

const FileListContainer = styled.div`
    display: flex;
    flex-direction: column;

    margin-top: 20px;
`;

const SpecificFileContainer = styled.div<{ uploadSuccess: boolean }>`
    height: 58px;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    padding: 18px 20px;
    opacity: ${({ uploadSuccess }) => (uploadSuccess ? '1' : '0.33')};
    /* margin-bottom: 12px; */
`;

const SuffixIcon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
`;

const FileLimitText = styled.div`
    white-space: pre-wrap;
    ${FontStyle(12, 20, '400', '#c8c8c8', -1)};
    text-align: center;
`;

export default FileAttachment;
